"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  StarIcon,
  ShieldCheckIcon,
  ClockIcon,
  HandThumbUpIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

// Icons are locale-independent, kept separate from the translated text so
// they can be zipped back together by index when rendering.
const ENGAGEMENT_ICONS = [ShieldCheckIcon, ClockIcon, HandThumbUpIcon];

const CONTENT = {
  fr: {
    h1: "Avis sur notre service de vide maison à Bruxelles",
    heroText:
      "Si vous cherchez des avis clients vérifiés sur notre entreprise, la réponse honnête est simple : nous démarrons notre activité de vide maison et débarras à Bruxelles, et aucun avis réel n'existe encore. Nous refusons d'afficher de faux témoignages pour paraître plus établis que nous ne le sommes. Nos premiers avis apparaîtront sur notre fiche Google dès que les premières interventions seront terminées, et nous les laisserons visibles tels quels, positifs comme négatifs.",
    ctaQuote: "Demander un devis gratuit",

    section1Title: "Pourquoi vous ne trouverez pas de faux avis ici",
    section1P1:
      "Beaucoup de sites de services affichent des étoiles et des témoignages dès leur ouverture, parfois recopiés d'un modèle générique ou inventés pour rassurer visuellement. Nous avons choisi de ne pas faire ça. Un avis client n'a de valeur que s'il reflète une intervention réelle, avec un client réel qui a pu juger notre travail après coup — pas avant.",
    section1P2Before:
      "Tant que ce n'est pas le cas, cette page reste honnête sur ce qu'elle peut et ne peut pas montrer. Vous pouvez en revanche consulter des exemples concrets de notre travail sur la page ",
    section1P2Link: "réalisations",
    section1P2After:
      ", ou nous contacter directement pour poser toutes vos questions avant de réserver une intervention.",

    section2Title: "Ce que nous nous engageons à offrir à chaque client",
    section2Sub:
      "En l'absence d'avis pour l'instant, voici concrètement ce sur quoi vous pouvez nous juger dès le premier contact.",
    engagementItems: [
      {
        title: "Sérieux et discrétion",
        text: "Une équipe respectueuse de votre logement et de votre situation, notamment lors d'une succession.",
      },
      {
        title: "Ponctualité",
        text: "Un créneau convenu est un créneau tenu, avec une intervention menée dans le délai annoncé au devis.",
      },
      {
        title: "Transparence",
        text: "Un devis clair avant intervention, sans frais cachés, et une estimation honnête des objets de valeur.",
      },
    ],

    section3Title: "Comment évaluer une entreprise qui n'a pas encore d'avis",
    section3P1:
      "Une entreprise récente n'est pas nécessairement moins fiable qu'une entreprise établie de longue date, mais elle doit le démontrer autrement. Voici les éléments concrets sur lesquels vous pouvez baser votre décision, indépendamment d'un historique d'avis :",
    checklist: [
      "La précision du devis reçu, gratuit et sans engagement",
      "La clarté des réponses données au téléphone ou par écrit",
      "Des exemples de chantiers réels sur la page réalisations",
      "Le détail des tarifs et des facteurs qui les font varier",
      "La possibilité de racheter vos objets de valeur, expliquée clairement",
      "La couverture des 19 communes de la Région bruxelloise",
    ],
    section3P2Before1: "Vous pouvez consulter nos ",
    section3P2Link1: "tarifs et les facteurs qui influencent le prix",
    section3P2Mid1: ", comprendre comment fonctionne le ",
    section3P2Link2: "rachat d'objets de valeur",
    section3P2Mid2: ", ou consulter directement la ",
    section3P2Link3: "page de votre commune",
    section3P2After: " pour vérifier que nous y intervenons.",

    section4Title: "Vous avez fait appel à nous ?",
    section4Text:
      "Votre avis compte énormément pour nous, surtout au démarrage de l'activité. Une fois votre intervention terminée, n'hésitez pas à nous laisser un avis sur notre fiche Google : c'est ce qui permettra à cette page de s'enrichir de témoignages réels, et à d'autres habitants de Bruxelles de se faire une idée sur base de faits, pas de promesses.",

    faqTitle: "Questions fréquentes",
    faq: [
      {
        q: "Pourquoi n'y a-t-il aucun avis client sur cette page ?",
        a: "Parce qu'il n'en existe pas encore. Vide Maison Bruxelles est un service qui démarre son activité dans la région bruxelloise, et nous n'avons pas encore assez d'interventions réalisées pour afficher des avis authentiques. Plutôt que d'inventer des témoignages ou d'en acheter, nous préférons l'assumer clairement.",
      },
      {
        q: "Est-ce risqué de choisir une entreprise sans avis en ligne ?",
        a: "C'est une question légitime. Sans historique d'avis, vous pouvez évaluer notre sérieux autrement : la clarté du devis proposé, la disponibilité au téléphone pour répondre à vos questions, et les exemples d'interventions déjà visibles sur notre page réalisations. Le devis reste gratuit et sans engagement, ce qui limite le risque de votre côté.",
      },
      {
        q: "Où pourrai-je consulter vos avis une fois qu'ils existeront ?",
        a: "Sur notre fiche Google, dès que les premières interventions auront eu lieu et que des clients y auront laissé leur retour. Nous ne créerons pas de page d'avis parallèle sur ce site : la fiche Google reste la source la plus fiable, car les avis y sont rattachés à un compte réel et ne peuvent pas être supprimés à notre convenance.",
      },
      {
        q: "Comment évaluer votre sérieux avant une première intervention ?",
        a: "Regardez comment nous communiquons dès le premier contact : un devis détaillé plutôt qu'un prix approximatif, des réponses précises à vos questions sur l'accès, le délai ou les objets de valeur, et la possibilité de voir des exemples concrets de chantiers sur notre page réalisations avant de vous engager.",
      },
      {
        q: "Puis-je vous poser des questions avant de réserver une intervention ?",
        a: "Oui, et c'est même recommandé tant que nous n'avons pas d'historique d'avis à vous montrer. Un appel ou un message via le formulaire de contact permet de clarifier le volume à évacuer, les contraintes d'accès de votre commune et le déroulement prévu, sans aucun engagement de votre part.",
      },
      {
        q: "Publierez-vous aussi les avis négatifs si vous en recevez ?",
        a: "Oui. Une fois notre fiche Google active, nous n'avons de toute façon aucun moyen de supprimer un avis légitime qui y serait déposé, positif ou négatif. C'est précisément ce qui rend une fiche Google plus fiable qu'un module d'avis géré directement par l'entreprise elle-même.",
      },
    ],

    ctaFinalTitle: "Une question avant de réserver ?",
    ctaFinalText:
      "Contactez-nous par téléphone ou via le formulaire ci-dessous. Nous répondons directement à vos questions sur le déroulement, le prix ou les délais, sans avis à l'appui, mais avec une explication claire de notre façon de travailler.",
    ctaFinalFooterBefore: "Envie de voir notre travail avant de nous contacter ? ",
    ctaFinalFooterLink1: "Consultez nos réalisations",
    ctaFinalFooterMid: " ou ",
    ctaFinalFooterLink2: "retrouvez toutes nos coordonnées",
    ctaFinalFooterAfter: ".",
  },
  nl: {
    h1: "Beoordelingen over onze dienst voor huis leegmaken in Brussel",
    heroText:
      "Als u op zoek bent naar geverifieerde klantenbeoordelingen over ons bedrijf, is het eerlijke antwoord eenvoudig: wij starten onze activiteit voor het leegmaken van huizen en ontruimingen in Brussel, en er bestaat nog geen enkele echte beoordeling. Wij weigeren nepgetuigenissen te tonen om gevestigder te lijken dan we zijn. Onze eerste beoordelingen zullen verschijnen op onze Google-bedrijfspagina zodra de eerste interventies zijn afgerond, en we laten ze zichtbaar zoals ze zijn, positief én negatief.",
    ctaQuote: "Vraag een gratis offerte aan",

    section1Title: "Waarom u hier geen nepbeoordelingen zult vinden",
    section1P1:
      "Veel dienstverleners tonen sterren en getuigenissen vanaf hun opening, soms overgenomen van een generiek model of verzonnen om visueel gerust te stellen. Wij hebben ervoor gekozen dat niet te doen. Een klantenbeoordeling heeft alleen waarde als ze een echte interventie weerspiegelt, met een echte klant die ons werk achteraf kon beoordelen — niet vooraf.",
    section1P2Before:
      "Zolang dat niet het geval is, blijft deze pagina eerlijk over wat ze wel en niet kan tonen. U kunt wel concrete voorbeelden van ons werk bekijken op onze pagina ",
    section1P2Link: "realisaties",
    section1P2After:
      ", of ons rechtstreeks contacteren om al uw vragen te stellen voordat u een interventie boekt.",

    section2Title: "Waar wij ons toe verbinden voor elke klant",
    section2Sub:
      "Bij gebrek aan beoordelingen tonen we hier concreet waarop u ons vanaf het eerste contact kunt beoordelen.",
    engagementItems: [
      {
        title: "Ernst en discretie",
        text: "Een team dat respect toont voor uw woning en uw situatie, in het bijzonder bij een nalatenschap.",
      },
      {
        title: "Stiptheid",
        text: "Een afgesproken tijdslot wordt ook gerespecteerd, met een interventie die binnen de in de offerte aangekondigde termijn wordt uitgevoerd.",
      },
      {
        title: "Transparantie",
        text: "Een duidelijke offerte vóór de interventie, zonder verborgen kosten, en een eerlijke schatting van waardevolle voorwerpen.",
      },
    ],

    section3Title: "Hoe beoordeelt u een bedrijf dat nog geen beoordelingen heeft?",
    section3P1:
      "Een recent bedrijf is niet noodzakelijk minder betrouwbaar dan een langgevestigd bedrijf, maar het moet dat op een andere manier aantonen. Hier zijn de concrete elementen waarop u uw beslissing kunt baseren, onafhankelijk van een geschiedenis van beoordelingen:",
    checklist: [
      "De nauwkeurigheid van de ontvangen offerte, gratis en vrijblijvend",
      "De duidelijkheid van de antwoorden die u telefonisch of schriftelijk krijgt",
      "Voorbeelden van echte werven op onze pagina realisaties",
      "Het detail van de tarieven en de factoren die ze doen variëren",
      "De mogelijkheid om uw waardevolle voorwerpen aan te kopen, duidelijk uitgelegd",
      "De dekking van de 19 gemeenten van het Brussels Gewest",
    ],
    section3P2Before1: "U kunt onze ",
    section3P2Link1: "tarieven en de factoren die de prijs beïnvloeden",
    section3P2Mid1: " raadplegen, begrijpen hoe de ",
    section3P2Link2: "inkoop van waardevolle voorwerpen",
    section3P2Mid2: " werkt, of rechtstreeks de ",
    section3P2Link3: "pagina van uw gemeente",
    section3P2After: " bekijken om te controleren of wij daar actief zijn.",

    section4Title: "Heeft u al een beroep op ons gedaan?",
    section4Text:
      "Uw mening telt enorm voor ons, zeker nu we net van start gaan. Zodra uw interventie is afgerond, aarzel dan niet om een beoordeling achter te laten op onze Google-bedrijfspagina: zo kan deze pagina zich verrijken met echte getuigenissen, en kunnen andere inwoners van Brussel zich een beeld vormen op basis van feiten, niet van beloftes.",

    faqTitle: "Veelgestelde vragen",
    faq: [
      {
        q: "Waarom staan er geen klantenbeoordelingen op deze pagina?",
        a: "Omdat ze nog niet bestaan. Vide Maison Bruxelles is een dienst die net van start gaat in het Brussels Gewest, en we hebben nog niet genoeg interventies uitgevoerd om authentieke beoordelingen te tonen. In plaats van getuigenissen te verzinnen of te kopen, kiezen we ervoor dit duidelijk toe te geven.",
      },
      {
        q: "Is het riskant om te kiezen voor een bedrijf zonder online beoordelingen?",
        a: "Dat is een terechte vraag. Zonder geschiedenis van beoordelingen kunt u onze betrouwbaarheid op een andere manier inschatten: de duidelijkheid van de voorgestelde offerte, de telefonische bereikbaarheid om uw vragen te beantwoorden, en de voorbeelden van interventies die al zichtbaar zijn op onze pagina realisaties. De offerte blijft gratis en vrijblijvend, wat het risico aan uw kant beperkt.",
      },
      {
        q: "Waar kan ik uw beoordelingen bekijken zodra ze er zijn?",
        a: "Op onze Google-bedrijfspagina, zodra de eerste interventies hebben plaatsgevonden en klanten daar hun feedback hebben achtergelaten. We zullen op deze site geen aparte beoordelingspagina aanmaken: de Google-bedrijfspagina blijft de meest betrouwbare bron, omdat de beoordelingen er gekoppeld zijn aan een echt account en niet naar believen door ons kunnen worden verwijderd.",
      },
      {
        q: "Hoe kunt u onze betrouwbaarheid inschatten vóór een eerste interventie?",
        a: "Kijk hoe wij communiceren vanaf het eerste contact: een gedetailleerde offerte in plaats van een ruwe schatting, precieze antwoorden op uw vragen over toegang, termijn of waardevolle voorwerpen, en de mogelijkheid om concrete voorbeelden van werven te bekijken op onze pagina realisaties voordat u zich engageert.",
      },
      {
        q: "Kan ik u vragen stellen voordat ik een interventie boek?",
        a: "Ja, en dat wordt zelfs aanbevolen zolang we geen geschiedenis van beoordelingen kunnen tonen. Een telefoontje of een bericht via het contactformulier maakt het mogelijk om het te evacueren volume, de toegangsbeperkingen van uw gemeente en het geplande verloop te verduidelijken, zonder enig engagement van uw kant.",
      },
      {
        q: "Zult u ook negatieve beoordelingen publiceren als u die ontvangt?",
        a: "Ja. Zodra onze Google-bedrijfspagina actief is, hebben we sowieso geen enkele manier om een legitieme beoordeling die daar wordt geplaatst te verwijderen, positief of negatief. Precies dat maakt een Google-bedrijfspagina betrouwbaarder dan een beoordelingsmodule die rechtstreeks door het bedrijf zelf wordt beheerd.",
      },
    ],

    ctaFinalTitle: "Nog een vraag voordat u boekt?",
    ctaFinalText:
      "Neem contact met ons op via telefoon of het formulier hieronder. Wij beantwoorden uw vragen over het verloop, de prijs of de termijnen rechtstreeks, zonder beoordelingen als bewijs, maar met een duidelijke uitleg van onze manier van werken.",
    ctaFinalFooterBefore: "Wilt u ons werk bekijken voordat u contact opneemt? ",
    ctaFinalFooterLink1: "Bekijk onze realisaties",
    ctaFinalFooterMid: " of ",
    ctaFinalFooterLink2: "vind al onze contactgegevens",
    ctaFinalFooterAfter: ".",
  },
  en: {
    h1: "Reviews of our house clearance service in Brussels",
    heroText:
      "If you're looking for verified customer reviews of our company, the honest answer is simple: we are just starting our house clearance and removal activity in Brussels, and no genuine reviews exist yet. We refuse to display fake testimonials to appear more established than we are. Our first reviews will appear on our Google Business Profile as soon as the first jobs are completed, and we will leave them visible as they are, positive and negative alike.",
    ctaQuote: "Request a free quote",

    section1Title: "Why you won't find fake reviews here",
    section1P1:
      "Many service providers display stars and testimonials from the moment they launch, sometimes copied from a generic template or invented purely to look reassuring. We chose not to do that. A customer review only has value if it reflects a real job, carried out for a real client who could judge our work afterwards — not beforehand.",
    section1P2Before:
      "Until that changes, this page stays honest about what it can and cannot show. You can, however, browse concrete examples of what we do on ",
    section1P2Link: "our work page",
    section1P2After:
      ", or contact us directly to ask all your questions before booking a job.",

    section2Title: "What we commit to offering every client",
    section2Sub:
      "In the absence of reviews for now, here is concretely what you can judge us on from the very first contact.",
    engagementItems: [
      {
        title: "Professionalism and discretion",
        text: "A team that respects your home and your situation, particularly during an estate clearance.",
      },
      {
        title: "Punctuality",
        text: "An agreed time slot is a time slot kept, with the job carried out within the timeframe stated in the quote.",
      },
      {
        title: "Transparency",
        text: "A clear quote before the job, with no hidden fees, and an honest appraisal of valuable items.",
      },
    ],

    section3Title: "How to evaluate a company that doesn't have reviews yet",
    section3P1:
      "A recently launched company is not necessarily less reliable than a long-established one, but it has to prove it differently. Here are the concrete elements you can base your decision on, independent of any review history:",
    checklist: [
      "The accuracy of the quote you receive, free and with no obligation",
      "The clarity of the answers given by phone or in writing",
      "Examples of real jobs on our work page",
      "The breakdown of prices and the factors that make them vary",
      "The possibility of buying back your valuable items, clearly explained",
      "Coverage of all 19 communes of the Brussels Region",
    ],
    section3P2Before1: "You can check our ",
    section3P2Link1: "prices and the factors that influence them",
    section3P2Mid1: ", find out how our ",
    section3P2Link2: "buyback of valuable items",
    section3P2Mid2: " works, or go directly to the ",
    section3P2Link3: "page for your commune",
    section3P2After: " to check that we operate there.",

    section4Title: "Have you already used our service?",
    section4Text:
      "Your opinion matters enormously to us, especially as we're just starting out. Once your job is complete, please don't hesitate to leave us a review on our Google Business Profile: that's what will allow this page to grow with real testimonials, and help other Brussels residents form an opinion based on facts, not promises.",

    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "Why aren't there any customer reviews on this page?",
        a: "Because none exist yet. Vide Maison Bruxelles is a service just starting out in the Brussels region, and we haven't yet completed enough jobs to display authentic reviews. Rather than inventing testimonials or buying them, we prefer to be upfront about it.",
      },
      {
        q: "Is it risky to choose a company with no online reviews?",
        a: "That's a fair question. Without a review history, you can judge our reliability in other ways: the clarity of the quote we provide, our availability by phone to answer your questions, and the examples of completed jobs already visible on our work page. The quote remains free and with no obligation, which limits the risk on your side.",
      },
      {
        q: "Where will I be able to see your reviews once they exist?",
        a: "On our Google Business Profile, as soon as the first jobs have taken place and clients have left their feedback there. We will not create a separate reviews page on this site: the Google Business Profile remains the most reliable source, because reviews there are tied to a real account and cannot be removed at our convenience.",
      },
      {
        q: "How can you assess our reliability before a first job?",
        a: "Look at how we communicate from the very first contact: a detailed quote rather than a rough estimate, precise answers to your questions about access, timing or valuable items, and the ability to see concrete examples of past jobs on our work page before committing.",
      },
      {
        q: "Can I ask you questions before booking a job?",
        a: "Yes, and it's even recommended while we don't yet have a review history to show you. A call or a message through the contact form lets us clarify the volume to be removed, the access constraints of your commune and the planned process, with no obligation on your part.",
      },
      {
        q: "Will you also publish negative reviews if you receive them?",
        a: "Yes. Once our Google Business Profile is active, we have no way of removing a legitimate review posted there anyway, whether positive or negative. That is precisely what makes a Google Business Profile more trustworthy than a review widget managed directly by the company itself.",
      },
    ],

    ctaFinalTitle: "A question before you book?",
    ctaFinalText:
      "Contact us by phone or via the form below. We answer your questions about the process, the price or the timing directly — without reviews to back it up, but with a clear explanation of how we work.",
    ctaFinalFooterBefore: "Want to see our work before contacting us? ",
    ctaFinalFooterLink1: "Check out our work",
    ctaFinalFooterMid: " or ",
    ctaFinalFooterLink2: "find all our contact details",
    ctaFinalFooterAfter: ".",
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "radial-gradient(900px 480px at 15% -10%, rgba(27,75,51,0.06), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1000px] px-6 py-20 text-center lg:px-10 lg:py-28">
          <StarIcon className="mx-auto h-10 w-10 text-accent" />
          <h1 className="mt-5 font-serif text-4xl font-black md:text-5xl">
            {c.h1}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* POURQUOI PAS D'AVIS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.section1Title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.section1P1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.section1P2Before}
            <Link href="/realisations" className="font-semibold text-accent underline underline-offset-4">
              {c.section1P2Link}
            </Link>
            {c.section1P2After}
          </p>
        </div>
      </section>

      {/* ENGAGEMENT QUALITE */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="text-center font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.section2Title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center leading-relaxed text-neutral-700">
            {c.section2Sub}
          </p>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {c.engagementItems.map((item, index) => {
              const Icon = ENGAGEMENT_ICONS[index];
              return (
                <div key={item.title} className="rounded-3xl border border-sand-line bg-white p-7 text-center">
                  <Icon className="mx-auto h-9 w-9 text-accent" />
                  <p className="mt-4 font-serif text-lg font-bold text-ink">{item.title}</p>
                  <p className="mt-2 text-sm text-neutral-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMENT NOUS JUGER SANS AVIS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.section3Title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.section3P1}
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.checklist.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 rounded-2xl border border-sand-line bg-sand p-4 text-sm font-semibold text-ink"
              >
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-8 leading-relaxed text-neutral-700">
            {c.section3P2Before1}
            <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
              {c.section3P2Link1}
            </Link>
            {c.section3P2Mid1}
            <Link href="/rachat-objets" className="font-semibold text-accent underline underline-offset-4">
              {c.section3P2Link2}
            </Link>
            {c.section3P2Mid2}
            <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.section3P2Link3}
            </Link>
            {c.section3P2After}
          </p>
        </div>
      </section>

      {/* LAISSER UN AVIS */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[800px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.section4Title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">
            {c.section4Text}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">{c.faqTitle}</h2>
          <div className="mt-8 space-y-4">
            {c.faq.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[1000px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.ctaFinalTitle}
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-neutral-700">
            {c.ctaFinalText}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
          <p className="mt-8 text-sm text-neutral-500">
            {c.ctaFinalFooterBefore}
            <Link href="/realisations" className="font-semibold text-accent underline underline-offset-4">
              {c.ctaFinalFooterLink1}
            </Link>
            {c.ctaFinalFooterMid}
            <Link href="/contact" className="font-semibold text-accent underline underline-offset-4">
              {c.ctaFinalFooterLink2}
            </Link>
            {c.ctaFinalFooterAfter}
          </p>
        </div>
      </section>

      <Contact />
    </div>
  );
}
